import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import Styles from './ImageSelection.module.scss'
import { ImageSelectionProps } from './Types'
import { Formik, useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { ImagePickerProvider, useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { SelectedImage } from 'types'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useConfirm } from './useConfirm'
import { Provider } from 'components/ImagePickerModal/Types'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { useEffect, useState } from 'react'
import { useImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { Heading } from 'blaze-ui/Heading/Heading'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { SelectImageContent } from './SelectImageContent'
import { compact } from 'lodash'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { isVideo } from 'components/ImagePickerModal/utils'
import cx from 'classnames'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

const getSubmitLabel = (
  schema: StepSchema,
  isSelectedVideo: boolean,
  showConfirmMedia: boolean,
  provider?: Provider,
) => {
  if (provider === 'ai') {
    return 'Use Generated Image'
  }

  if (isSelectedVideo) {
    if (showConfirmMedia) return schema.describe().meta.videoConfirmLabel || 'Add Video'

    return 'Confirm Selected Video'
  }

  return schema.describe().meta.submitLabel
}

const FormikContent = (props: ImageSelectionProps) => {
  const {
    schema,
    isLoading,
    onBack,
    currentStep,
    confirmImage,
    confirmVideo,
    onSubmit,
    loadedPercentage,
    setLoadedPercentage,
    inModal,
  } = props
  const { setFieldValue, submitForm, isSubmitting } = useFormikContext()
  const [showConfirmMedia, setShowConfirmMedia] = useState<boolean>(false)
  const { selectedImages, setSelectedImages, provider, setProvider, isAIGenerating, isUploading } =
    useImagePickerContext()

  const showButtons = ['ai', 'stock', 'link'].includes(provider || '') || showConfirmMedia
  const { header, previousLabel, withBackArrow } = schema.describe().meta

  const isSelectedVideo = isVideo({
    url: selectedImages[0]?.source_url || selectedImages[0]?.url,
    type: selectedImages[0]?.mime_type,
  })

  const handleSubmit = () => {
    // If not confirming a video, go to next step
    if (!confirmVideo || !isSelectedVideo) {
      submitForm()
      return
    }

    // If video needs to be confirmed, submitting the step will show the confirm screen on this same step (all the uploads logic is in this step).
    if (!showConfirmMedia) {
      submitForm()
      setShowConfirmMedia(true)
      setLoadedPercentage?.(0)
    } else {
      // Submitting a 2nd time while on the confirmation step, will call the step submission callback.
      onSubmit?.({ initial_media_file: selectedImages[0] })
    }
  }

  useEffect(() => {
    setFieldValue('initial_media_file_id', selectedImages?.[0]?.id || selectedImages?.[0]?.unsplash?.id)
    setFieldValue('initial_media_file', selectedImages?.[0])
    setFieldValue(
      'selected_images',
      selectedImages?.map((image) => image.url),
    )

    // Reset topic field if a different image is selected
    setFieldValue('topic', undefined)
  }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form.Root>
      <Form.Section>
        <div className={cx(Styles.container, { [Styles.inModal]: inModal })}>
          {!showConfirmMedia && !inModal && <Heading level={1}>{header}</Heading>}
          {inModal && provider === 'stock' && (
            <div className={Styles.modalHeader}>
              <div className={Styles.left}>
                <IconButton variant="tertiary" size="xs" onPress={onBack} icon={ArrowLeft} />
                <Heading level={3}>{header}</Heading>
              </div>
              <Form.PrimaryActions>
                <Button
                  variant="tertiary"
                  size="md"
                  isDisabled={!selectedImages.length}
                  onPress={() => setSelectedImages([])}
                >
                  Deselect
                </Button>
                <Button
                  size="md"
                  onPress={handleSubmit}
                  isDisabled={isSubmitting || isAIGenerating || isLoading || isUploading || !selectedImages.length}
                >
                  {getSubmitLabel(schema, isSelectedVideo, showConfirmMedia, provider)}
                </Button>
              </Form.PrimaryActions>
            </div>
          )}
          <SelectImageContent
            onFinalizeImageSelection={submitForm}
            showConfirmMedia={showConfirmMedia}
            setShowConfirmMedia={setShowConfirmMedia}
            confirmImage={confirmImage}
            confirmVideo={confirmVideo}
            loadedPercentage={loadedPercentage}
          />
        </div>
      </Form.Section>

      {(!inModal || provider !== 'stock') && (
        <Form.Actions isSticky>
          {showButtons && (
            <Form.PreviousButton
              onPress={() => {
                setProvider('uploads')
                setShowConfirmMedia(false)
              }}
            >
              {confirmVideo ? 'Image & Video Options' : 'Image Options'}
            </Form.PreviousButton>
          )}
          {currentStep > 0 && (
            <Form.PreviousButton
              onPress={onBack}
              isDisabled={isSubmitting || isUploading || isLoading}
              frontIcon={withBackArrow ? ArrowLeft : null}
              size={inModal ? 'lg' : 'md'}
            >
              {previousLabel}
            </Form.PreviousButton>
          )}
          <Form.PrimaryActions>
            <Button
              size="lg"
              endIcon={ArrowRight}
              onPress={handleSubmit}
              isDisabled={isSubmitting || isAIGenerating || isLoading || isUploading || !selectedImages.length}
            >
              {getSubmitLabel(schema, isSelectedVideo, showConfirmMedia, provider)}
            </Button>
          </Form.PrimaryActions>
        </Form.Actions>
      )}
    </Form.Root>
  )
}

const Content = (props: ImageSelectionProps) => {
  const { schema, initialValues, onSubmit, onChange, loadedPercentage, setLoadedPercentage } = props
  const { selectedImages, provider } = useImagePickerContext()
  const { confirm } = useConfirm()

  // Callback to set whether the selected media is an image or not on the parent component
  useEffect(() => onChange?.(selectedImages[0]), [onChange, selectedImages])

  const handleSubmit = async (values: any) => {
    if (!values.initial_media_file) {
      await onSubmit(values)

      return
    }

    const isUnsplashedImage = !!selectedImages[0]?.unsplash

    const isSelectedVideo = isVideo({
      url: selectedImages[0]?.source_url || selectedImages[0]?.url,
      type: selectedImages[0]?.mime_type,
    })

    // Show toast only when uploading an image from stock, link or ai providers
    let generatingToast
    if ((provider !== 'uploads' || isUnsplashedImage) && !isSelectedVideo)
      generatingToast = showToastMessage('Uploading image...', 'loading', undefined, 0)

    await confirm()

    // When not uploading an image from device, the submission is handled on the provider's 'useConfirm' hook.
    if (provider === 'uploads' && !isSelectedVideo && !!values.initial_media_file.id) {
      await onSubmit(values)
    }

    generatingToast?.()
  }

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={handleSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <FormikContent
        {...props}
        onSubmit={onSubmit}
        loadedPercentage={loadedPercentage}
        setLoadedPercentage={setLoadedPercentage}
      />
    </Formik>
  )
}

export const ImageSelection = multiStep<ImageSelectionProps>((props) => {
  const [loadedPercentage, setLoadedPercentage] = useState<number>()
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(
    false,
    () => setLoadedPercentage(undefined),
    setLoadedPercentage,
    { forceExtractBackgroundJobSync: true }, // need full metadata to generate designs
  )
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { generateImageKeywords, imageKeywords, topic: previousTopic } = useImages()

  const topic = props.initialValues.topic

  const handleSubmit = (image: SelectedImage) => {
    return props.onSubmit({
      initial_media_file_id: image?.id, // Used to set the image to be send to the server
      initial_media_file: image, // Used to pass the image to the background removal step
      selected_images: image.mime_type?.startsWith('image') ? compact([image?.thumbnail_url || image?.url]) : undefined, // Used to pass the images to doc generation
      selected_videos: image.mime_type?.startsWith('video')
        ? [
            {
              duration: image?.duration,
              height: image?.height,
              mime_type: image?.mime_type,
              source_url: image?.source_url,
              thumbnail_url: image?.thumbnail_url,
              width: image?.width,
            },
          ]
        : undefined,
    })
  }

  useEffect(() => {
    if (topic && topic !== previousTopic) generateImageKeywords({ topic })
  }, [generateImageKeywords, previousTopic, props.initialValues.topic, topic])

  return (
    <ImagePickerProvider
      defaultProvider="uploads"
      accept={props.accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
      maxCount={1}
      uploadImages={async (files) => {
        let uploadedImages
        uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId)

        if (!uploadedImages) return []
        return uploadedImages.map((file) => ({ ...file, url: file.source_url } as SelectedImage))
      }}
      initialQuery={imageKeywords.join(' ') || ''}
      onConfirm={async (images) => {
        const isSelectedVideo = isVideo({
          url: images[0]?.source_url || images[0]?.url,
          type: images[0]?.mime_type,
        })

        // When confirming a video, the submission will be handled by the inner 'Content' component
        if (isSelectedVideo && props.confirmVideo) return
        if (!images[0]?.id) return

        handleSubmit(images[0])
      }}
      hiddenProviders={new Set<Provider>(['gifs'])}
    >
      <Content
        {...props}
        onSubmit={(values) => handleSubmit(values.initial_media_file)}
        loadedPercentage={loadedPercentage}
        setLoadedPercentage={setLoadedPercentage}
      />
    </ImagePickerProvider>
  )
})
